import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import type { Database } from '@/types/database.types';

type Tables = Database['public']['Tables'];

// Colonnes nécessaires pour les cartes d'articles (listing)
const ARTICLE_LIST_FIELDS = 'id, slug, title, excerpt, featured_image, category, tags, reading_time, published_at, created_at, is_premium, price, word_count, lang' as const;

// Articles hooks
export function useArticles(filters?: {
  category?: string;
  tags?: string[];
  isPremium?: boolean;
  limit?: number;
  offset?: number;
  lang?: string;
}) {
  return useQuery({
    queryKey: ['articles', filters],
    queryFn: async () => {
      let query = supabase
        .from('articles')
        .select(ARTICLE_LIST_FIELDS)
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      // CRITICAL: Filter by language
      if (filters?.lang) {
        query = query.eq('lang', filters.lang);
      }

      if (filters?.category) {
        query = query.eq('category', filters.category);
      }

      if (filters?.isPremium !== undefined) {
        query = query.eq('is_premium', filters.isPremium);
      }

      if (filters?.tags && filters.tags.length > 0) {
        query = query.contains('tags', filters.tags);
      }

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Si ce n'est pas la langue FR, charger les images depuis les versions FR
      if (data && filters?.lang && filters.lang !== 'fr') {
        // Récupérer tous les slugs uniques
        const slugs = [...new Set(data.map(article => article.slug))];

        // Charger toutes les images FR en une seule requête
        const { data: frArticles } = await supabase
          .from('articles')
          .select('slug, featured_image')
          .in('slug', slugs)
          .eq('lang', 'fr')
          .eq('status', 'published');

        // Créer un map slug -> featured_image pour performance
        const frImagesMap = new Map(
          frArticles?.map(article => [article.slug, article.featured_image]) || []
        );

        // Remplacer les images par celles de la version FR
        data.forEach(article => {
          const frImage = frImagesMap.get(article.slug);
          if (frImage) {
            article.featured_image = frImage;
          }
        });
      }

      return data as Tables['articles']['Row'][];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes de cache
  });
}

// Hook pour compter le nombre total d'articles (pour la pagination)
export function useArticlesCount(filters?: {
  category?: string;
  tags?: string[];
  isPremium?: boolean;
  lang?: string;
}) {
  return useQuery({
    queryKey: ['articles-count', filters],
    queryFn: async () => {
      let query = supabase
        .from('articles')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'published');

      // CRITICAL: Filter by language
      if (filters?.lang) {
        query = query.eq('lang', filters.lang);
      }

      if (filters?.category) {
        query = query.eq('category', filters.category);
      }

      if (filters?.isPremium !== undefined) {
        query = query.eq('is_premium', filters.isPremium);
      }

      if (filters?.tags && filters.tags.length > 0) {
        query = query.contains('tags', filters.tags);
      }

      const { count, error } = await query;

      if (error) throw error;

      return count || 0;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes de cache
  });
}

export function useArticle(slug: string, lang?: string) {
  return useQuery({
    queryKey: ['article', slug, lang],
    queryFn: async () => {
      let query = supabase
        .from('articles')
        .select('*')
        .eq('slug', slug);

      // CRITICAL: Filter by language if provided
      if (lang) {
        query = query.eq('lang', lang);
      }

      const { data, error } = await query.single();

      if (error) throw error;

      // Si ce n'est pas la version FR, charger l'image depuis la version FR
      if (data && lang && lang !== 'fr') {
        const { data: frArticle } = await supabase
          .from('articles')
          .select('featured_image')
          .eq('slug', slug)
          .eq('lang', 'fr')
          .eq('status', 'published')
          .single();

        // Remplacer featured_image par celle de la version FR si disponible
        if (frArticle?.featured_image) {
          data.featured_image = frArticle.featured_image;
        }
      }

      return data as Tables['articles']['Row'];
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000, // 5 minutes de cache
  });
}

// Hook pour charger uniquement l'image featured depuis la version FR
export function useFeaturedImage(slug: string) {
  return useQuery({
    queryKey: ['featured-image', slug],
    queryFn: async () => {
      // Toujours charger l'image depuis la version FR
      const { data, error } = await supabase
        .from('articles')
        .select('featured_image')
        .eq('slug', slug)
        .eq('lang', 'fr')
        .eq('status', 'published')
        .single();

      if (error) throw error;
      return data?.featured_image || null;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000, // 5 minutes de cache
  });
}

// Formations hooks
export function useFormations() {
  return useQuery({
    queryKey: ['formations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('formations')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Tables['formations']['Row'][];
    },
  });
}

export function useFormation(slug: string) {
  return useQuery({
    queryKey: ['formation', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('formations')
        .select('*, formation_sessions(*)')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });
}

// Newsletter subscription
export function useNewsletterSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .insert({ email, status: 'active' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter'] });
    },
  });
}

// Contact form submission (via Edge Function pour envoi d'emails)
export function useContactForm() {
  return useMutation({
    mutationFn: async (data: { name: string; email: string; phone?: string | null; company?: string | null; message: string }) => {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: data.name,
          email: data.email,
          phone: data.phone || null,
          company: data.company || null,
          message: data.message,
        },
      });

      if (error) throw error;
    },
  });
}
